import React, { useState, useEffect } from "react";

// Supabase
import { supabase } from "../../supabase";

// Context
import { useAuth } from "../../contexts/AuthContext";

// React Icons
import {
    FaCheckSquare,
    FaTrash,
    FaSortAmountDownAlt,
    FaFilter,
} from "react-icons/fa";

// Components
import JobsTable from "./components/JobsTable";
import JobsTableLoader from "./components/JobsTableLoader";

// Utils
import { getCategoryClass } from "./utils/helpers";


const JobApplicationsTrackerPage = () => {
    const { user } = useAuth();

    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "asc",
    });
    const [jobs, setJobs] = useState([]);
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) return;
        fetchBookmarkedJobs();
    }, [user]);

    const fetchBookmarkedJobs = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from("my_applications")
                .select(
                    `
                    *,
                    jobs:job_id (
                        id,
                        company,
                        location,
                        continent,
                        title,
                        fulltime_parttime,
                        experience,
                        category,
                        deadline,
                        application_url,
                        created_at,
                        last_updated,
                        admin_notes,
                        live,
                        date_added,
                        sheet_row
                    )
                `
                )
                .eq("user_id", user.id)
                .order("created_at", { ascending: false });

            if (error) throw error;

            const transformedJobs = data.map((item) => ({
                ...item.jobs,
                status: item.status,
                my_application_id: item.id,
            }));

            setJobs(transformedJobs);
        } catch (error) {
            console.error("Error fetching bookmarked jobs:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (jobId, newStatus) => {
        try {
            const job = jobs.find((j) => j.id === jobId);
            if (!job) {
                return;
            }

            setJobs((prevJobs) =>
                prevJobs.map((job) =>
                    job.id === jobId ? { ...job, status: newStatus } : job
                )
            );

            const { error } = await supabase
                .from("my_applications")
                .update({
                    status: newStatus,
                    updated_at: new Date().toISOString(),
                })
                .eq("id", job.my_application_id);

            if (error) {
                throw error;
            }
        } catch (error) {
            fetchBookmarkedJobs();
        }
    };

    const handleDelete = async () => {
        if (selectedJobs.length === 0) return;

        try {
            const jobsToDelete = jobs
                .filter((job) => selectedJobs.includes(job.id))
                .map((job) => job.my_application_id);

            const { error } = await supabase
                .from("my_applications")
                .delete()
                .in("id", jobsToDelete);

            if (error) throw error;

            setJobs((prevJobs) =>
                prevJobs.filter((job) => !selectedJobs.includes(job.id))
            );
            setSelectedJobs([]);
        } catch (error) {
            console.error("Error deleting jobs:", error);
        }
    };

    const statusOptions = [
        "all",
        ...new Set(jobs.map((job) => job.status || "applied")),
    ];

    const filteredJobs =
        statusFilter === "all"
            ? jobs
            : jobs.filter((job) => job.status === statusFilter);

    const sortedJobs = [...filteredJobs].sort((a, b) => {
        if (!sortConfig.key) return 0;

        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) {
            return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
    });

    const handleSelectAll = () => {
        if (selectedJobs.length === sortedJobs.length) {
            setSelectedJobs([]);
        } else {
            setSelectedJobs(sortedJobs.map((job) => job.id));
        }
    };

    const handleJobSelection = (jobId) => {
        setSelectedJobs((prev) =>
            prev.includes(jobId)
                ? prev.filter((id) => id !== jobId)
                : [...prev, jobId]
        );
    };

    if (!user) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-black">
                    Please log in or sign up to view your applications
                </div>
            </div>
        );
    }

    if (loading) {
        return <JobsTableLoader />;
    }

    return (
        <div className="flex flex-col min-h-screen bg-white">
            <main className="main-content">
                <div className="content-container">
                    <div className="flex md items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <h2 className="text-2xl font-bold">
                                My Applications
                            </h2>
                        </div>
                    </div>

                    <div className="flex md:flex-row flex-col items-center justify-between mb-6">
                        <div className="flex items-center gap-3 sm:gap-4 w-full md:w-auto">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                                <button
                                    className={`px-2 py-1 text-sm sm:px-3 sm:text-base rounded-full border-2 ${
                                        selectedJobs.length > 0
                                            ? "border-blue-500 text-blue-500"
                                            : "border-gray-300 text-black"
                                    } flex items-center gap-1 hover:bg-gray-50`}
                                    onClick={handleSelectAll}
                                    disabled={jobs.length === 0}
                                    style={{
                                        opacity: jobs.length === 0 ? 0.5 : 1,
                                    }}
                                >
                                    <FaCheckSquare className="text-base" 
                                        style={{
                                            opacity:
                                                jobs.length === 0 ? 0.5 : 1,  
                                        }}
                                    />
                                    Selected{" "}
                                    {selectedJobs.length > 0 &&
                                        `(${selectedJobs.length})`}
                                </button>

                                <div className="relative">
                                    <div
                                        className={`inline-flex items-center gap-1 px-2 py-1 text-sm sm:px-3 sm:text-base rounded-full border-2 ${
                                            statusFilter !== "all"
                                                ? "border-blue-500 text-blue-500"
                                                : "border-gray-300 text-black"
                                        }`}
                                    >
                                        <FaFilter
                                            className="text-base"
                                            style={{
                                                opacity:
                                                    jobs.length === 0 ? 0.5 : 1,
                                            }}
                                        />
                                        <select
                                            className="bg-transparent border-none focus:outline-none text-inherit"
                                            value={statusFilter}
                                            onChange={(e) =>
                                                setStatusFilter(e.target.value)
                                            }
                                            disabled={jobs.length === 0}
                                        >
                                            {statusOptions.map((status) => (
                                                <option
                                                    key={status}
                                                    value={status}
                                                >
                                                    {status === "all"
                                                        ? "All Statuses"
                                                        : status
                                                              .charAt(0)
                                                              .toUpperCase() +
                                                          status.slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* force line break after second pill on mobile */}
                                <span className="basis-full h-0 sm:hidden"></span>

                                <button
                                    className={`px-2 py-1 text-sm sm:px-3 sm:text-base rounded-full border ${
                                        selectedJobs.length > 0
                                            ? "border-red-500 text-red-500 hover:bg-red-50"
                                            : "border-gray-300 text-gray-400"
                                    } flex items-center gap-1`}
                                    onClick={handleDelete}
                                    disabled={selectedJobs.length === 0}
                                >
                                    <FaTrash className="text-base" /> Delete{" "}
                                    {selectedJobs.length > 0 &&
                                        `(${selectedJobs.length})`}
                                </button>

                                {/* mobile-only Sort button to sit next to Delete */}
                                <button
                                    className="md:hidden ml-4 px-2 py-1 text-sm rounded-full border border-gray-300 text-gray-700 flex items-center gap-1 whitespace-nowrap max-w-[160px]"
                                    onClick={() => setSortConfig(prev => ({
                                        key: 'created_at',
                                        direction: prev.direction === 'asc' ? 'desc' : 'asc'
                                    }))}
                                >
                                    Sort by{" "}
                                    <FaSortAmountDownAlt className="text-base" />
                                    <span className="ml-1">
                                        {sortConfig.direction === 'asc' ? 'Oldest' : 'Newest'}
                                    </span>
                                </button>
                            </div>
                        </div>
                        <div className="relative hidden md:block">
                            <button 
                                className="px-3 py-1 rounded-full border border-gray-300 text-gray-700 flex items-center gap-1"
                                onClick={() => setSortConfig(prev => ({
                                    key: 'created_at',
                                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                                }))}
                            >
                                Sort by{" "}
                                <FaSortAmountDownAlt className="text-base" />
                                <span className="ml-1">
                                    {sortConfig.direction === 'asc' ? 'Oldest' : 'Newest'}
                                </span>
                            </button>
                        </div>
                    </div>

                    <JobsTable
                        jobs={sortedJobs}
                        user={true}
                        getCategoryClass={getCategoryClass}
                        showBlurEffect={false}
                        selectable={true}
                        showStatus={true}
                        selectedJobs={selectedJobs}
                        onJobSelect={handleJobSelection}
                        onStatusChange={handleStatusChange}
                    />
                </div>
            </main>
        </div>
    );
};

export default JobApplicationsTrackerPage;
